/* PANDA FOOD 2.0, ambient light and scroll motion.
   The homepage hero's drifting light and its entrance vocabulary, carried
   to the sections that matter most. Styles live in pf2-ambient.css; the
   header comment there explains the system. This file only decides where
   it applies, and does nothing for a visitor who asked for reduced motion
   beyond adding the (still) light. */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var canIO = 'IntersectionObserver' in window;

  /* ---------- 1. Light: every section that helps someone buy ---------- */
  /* Section roots, not their contents. The hero carries its own light, and
     the header, announcement bar, offer strip, popup, cart and bundle builder
     are left alone: they are tools, not moments. The footer, with its email
     signup, stays still at Ryan's request (26 Sep 2026). */
  var ROOTS = [
    '.pf2-pdp', '.pf2-col', '.pf2-video', '.pf2-rel', '.pf2-feat', '.pf2-mt', '.pf2-duo',
    '.pf2-steps', '.pf2-story', '.pf2-cats', '.pf2-rev', '.pf2-assure', '.pf2-faq',
    '.pf2-life', '.pf2-gifts', '.pf2-b21', '.pf2-stats', '.pf2-comp', '.pf2-size',
    '.pf2-spec', '.pf2-gal', '.pf2-sechead', '.pf2-page'
  ].join(',');
  var POOLS = '<span class="pf2-glow-a"><i></i></span><span class="pf2-glow-b"><i></i></span><span class="pf2-glow-c"><i></i></span>';

  /* The palette follows the ground each section actually paints, read from
     the design tokens, so a new section or an event remap needs no list. */
  function grounds() {
    var probe = document.createElement('div'), map = {};
    probe.style.display = 'none';
    document.body.appendChild(probe);
    [['--green-900', 'dark'], ['--green-800', 'dark'], ['--green-700', 'dark'],
     ['--green-100', 'mint'], ['--cream', 'light'], ['--sun', 'sun']].forEach(function (t) {
      probe.style.backgroundColor = 'var(' + t[0] + ')';
      map[getComputedStyle(probe).backgroundColor] = t[1];
    });
    map['rgb(255, 255, 255)'] = map['rgb(255, 255, 255)'] || 'light';
    probe.remove();
    return map;
  }

  function light() {
    var hosts = [], tone = grounds();
    document.querySelectorAll(ROOTS).forEach(function (el) {
      if (el.classList.contains('pf2-glow-host')) return;
      var ground = tone[getComputedStyle(el).backgroundColor];
      if (!ground) return;
      var g = document.createElement('div');
      g.className = 'pf2-glow pf2-glow--' + ground;
      g.setAttribute('aria-hidden', 'true');
      g.innerHTML = POOLS;
      if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
      el.classList.add('pf2-glow-host');
      /* appended, not prepended, so no :first-child rule in a section shifts */
      el.appendChild(g);
      hosts.push(el);
    });

    /* the product page wave: rewrite its one period as two, so it can drift
       seamlessly like the hero's. Only the known path is touched. */
    var ONE = 'M0 90V44c180-40 420-40 720 0s540 40 720 0V90z';
    var TWO = 'M0 90V44c180-40 420-40 720 0s540 40 720 0s420-40 720 0s540 40 720 0V90z';
    document.querySelectorAll('.pf2-pdp-edge').forEach(function (edge) {
      var svg = edge.querySelector('svg'), path = svg && svg.querySelector('path');
      if (!path || path.getAttribute('d') !== ONE) return;
      path.setAttribute('d', TWO);
      svg.setAttribute('viewBox', '0 0 2880 90');
      edge.classList.add('pf2-drift');
    });

    /* nothing loops while its section is off screen */
    if (canIO && hosts.length) {
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (e) { e.target.classList.toggle('is-still', !e.isIntersecting); });
      });
      hosts.forEach(function (h) { io.observe(h); });
    }
  }

  /* ---------- 2. Motion: entrances, once, as each section arrives ---------- */
  var RISE = [
    '.pf2-feat-head h2', '.pf2-story-copy h2', '.pf2-mt-copy h2', '.pf2-steps-head h2',
    '.pf2-video-title', '.pf2-rel-head h2', '.pf2-cats-head h2', '.pf2-life-top h2', '.pf2-duo-copy h2',
    '.pf2-faq-head h2', '.pf2-gifts-head h2', '.pf2-b21-head h2', '.pf2-stats-head h2', '.pf2-comp-head h2',
    '.pf2-size-head h2', '.pf2-spec-head h2', '.pf2-gal-head h2', '.pf2-sechead-in h2'
  ].join(',');
  var SETTLE = [
    '.pf2-feat-head > p', '.pf2-story-copy > :not(h2)', '.pf2-mt-copy > :not(h2)', '.pf2-steps-head > p',
    /* the lists and proof that do the persuading, one line at a time */
    '.pf2-video-list > li', '.pf2-video-best', '.pf2-duo-list > li', '.pf2-spec-list > li', '.pf2-comp-list > li',
    '.pf2-assure-item', '.pf2-faq-card > details', '.pf2-stat', '.pf2-size-note',
    '.pf2-rel-head > p', '.pf2-cats-head > p', '.pf2-faq-head > p', '.pf2-gifts-head > p', '.pf2-b21-head > p',
    '.pf2-stats-head > p', '.pf2-comp-head > p', '.pf2-size-head > p', '.pf2-spec-head > p', '.pf2-gal-head > p',
    '.pf2-sechead-in > p'
  ].join(',');
  /* the steps band tells its own story: the line draws left to right and
     each number arrives as the line reaches it */
  var STEPS = '.pf2-steps-flow';
  var CARDS = '.pf2-feat-card, .pf2-col-card, .pf2-rel-card, .pf2-cat, .pf2-gcard, .pf2-comp-card';
  var UNFURL = '.pf2-story-fig, .pf2-mt-fig, .pf2-duo-fig, .pf2-video-frame';

  /* headings are split into words inside masks; the whole heading stays
     readable as one line through aria-label */
  function split(h) {
    h.setAttribute('aria-label', h.textContent.replace(/\s+/g, ' ').trim());
    var n = 0;
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (c) {
        if (c.nodeType === 3) {
          var frag = document.createDocumentFragment();
          c.nodeValue.split(/(\s+)/).forEach(function (p) {
            if (!p) return;
            if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(' ')); return; }
            var w = document.createElement('span'), s = document.createElement('span');
            w.className = 'pf2m-w'; w.setAttribute('aria-hidden', 'true');
            s.textContent = p; s.style.setProperty('--i', n++);
            w.appendChild(s); frag.appendChild(w);
          });
          node.replaceChild(frag, c);
        } else if (c.nodeType === 1 && c.tagName !== 'BR') {
          walk(c);
        }
      });
    })(h);
  }

  function motion() {
    if (reduce || !canIO) return;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var queue = [];
    /* anything already on screen stays exactly as it is: no flash */
    function offscreen(el) { return el.getBoundingClientRect().top > vh * 0.92; }
    function prep(sel, cls, extra) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (el.hasAttribute('data-pf2m') || !offscreen(el)) return;
        el.setAttribute('data-pf2m', '');
        if (cls === 'pf2m-rise') split(el);
        if (cls === 'pf2m-unfurl' && !el.querySelector('img, video')) return;
        el.classList.add(cls);
        if (extra) el.classList.add(extra);
        queue.push(el);
      });
    }
    prep(RISE, 'pf2m-rise');
    prep(SETTLE, 'pf2m-settle');
    prep(CARDS, 'pf2m-settle', 'pf2m-card');
    prep(UNFURL, 'pf2m-unfurl');
    document.querySelectorAll(STEPS).forEach(function (ol) {
      var li = ol.children;
      if (ol.hasAttribute('data-pf2m') || li.length < 2 || !offscreen(ol)) return;
      ol.setAttribute('data-pf2m', '');
      for (var i = 0; i < li.length; i++) li[i].style.setProperty('--pf2m-at', (i / (li.length - 1) * 1.15).toFixed(2) + 's');
      ol.classList.add('pf2m-steps');
      queue.push(ol);
    });
    if (!queue.length) return;

    function arrive(el, delay) {
      el.style.setProperty('--pf2m-d', delay + 's');
      el.classList.add('is-in');
      /* once it has landed, hand the element back to its own styles */
      setTimeout(function () {
        el.classList.remove('pf2m-rise', 'pf2m-settle', 'pf2m-card', 'pf2m-unfurl', 'pf2m-steps', 'is-in');
        el.style.removeProperty('--pf2m-d');
      }, (delay + 2.8) * 1000);
    }

    var io = new IntersectionObserver(function (es) {
      /* elements arriving together go in document order, a beat apart */
      var k = 0;
      es.filter(function (e) { return e.isIntersecting; })
        .sort(function (a, b) {
          return a.target.compareDocumentPosition(b.target) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        })
        .forEach(function (e) {
          io.unobserve(e.target);
          arrive(e.target, Math.min(k++ * 0.08, 0.48));
        });
    }, { rootMargin: '0px 0px -10% 0px' });
    queue.forEach(function (el) { io.observe(el); });

    /* belt and braces: nothing is ever left waiting */
    function flush() { queue.forEach(function (el) { if (!el.classList.contains('is-in') && /pf2m-(rise|settle|unfurl|steps)/.test(el.className)) { io.unobserve(el); arrive(el, 0); } }); }
    window.addEventListener('beforeprint', flush);
    window.addEventListener('load', function () {
      setTimeout(function () {
        queue.forEach(function (el) {
          if (el.classList.contains('is-in')) return;
          var r = el.getBoundingClientRect();
          if (r.top < vh && r.bottom > 0) { io.unobserve(el); arrive(el, 0); }
        });
      }, 5000);
    });
  }

  function run() { light(); motion(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
